import React from 'react';
import PropTypes from 'prop-types';
import { fontSizes, fontWeights, space } from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Grid, Text } from 'components/ui';
import Chevron from 'components/Chevron';

const LEFT_COLUMN_WIDTH = 108;

/**
 * SubscriptionInfoSection - Reusable section component for subscription information display
 *
 * Displays a titled section with optional interactive functionality (clickable to edit).
 * Used in ManageSubscriptionModal for delivery, shipping, and payment sections.
 *
 * @component
 */
function SubscriptionInfoSection({
    ariaLabel, children, isInteractive, onClick, title
}) {
    const isClickable = isInteractive && onClick;

    return (
        <Grid
            css={[styles.tabContainer, isClickable && styles.interactiveTab]}
            onClick={isClickable ? onClick : undefined}
            aria-label={isClickable ? ariaLabel : undefined}
        >
            <Text
                is='h4'
                css={styles.tabHeading}
            >
                {title}
            </Text>
            <Box>{children}</Box>
            {isClickable && (
                <Chevron
                    isThicker={true}
                    css={styles.chevron}
                    direction='right'
                />
            )}
        </Grid>
    );
}

const styles = {
    tabContainer: {
        alignItems: 'center',
        gridTemplateColumns: `${LEFT_COLUMN_WIDTH}px 1fr`,
        paddingRight: space[4],
        position: 'relative'
    },
    interactiveTab: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%'
    },
    tabHeading: {
        fontSize: fontSizes.base,
        fontWeight: fontWeights.bold
    },
    chevron: {
        position: 'absolute',
        right: '0',
        top: '50%',
        transform: 'translate(0, -50%)'
    }
};

SubscriptionInfoSection.propTypes = {
    ariaLabel: PropTypes.string,
    children: PropTypes.node.isRequired,
    isInteractive: PropTypes.bool,
    onClick: PropTypes.func,
    title: PropTypes.string.isRequired
};

export default wrapFunctionalComponent(SubscriptionInfoSection, 'SubscriptionInfoSection');
